import { calculateProject, getSurfaceDimensions } from '../../shared/calculations/layoutEngine';
import { calculateMaterials } from '../../shared/calculations/materials';
import { ProjectState, TileCategory } from '../../shared/types';
import { useProjectStore } from '../store/useProjectStore';
import { Field, SectionTitle, Toggle } from './Controls';
import { Icon } from './Icon';

const categoryLabels: Record<TileCategory, string> = {
  floor: 'Половая плитка',
  darkWall: 'Стеновая тёмная',
  lightWall: 'Стеновая светлая',
  decor: 'Декор',
  border: 'Фриз',
};

const formatNumber = (value: number, digits = 1) =>
  new Intl.NumberFormat('ru-RU', { maximumFractionDigits: digits }).format(value);

const formatMoney = (value: number) =>
  `${Math.round(value).toLocaleString('ru-RU')} ₽`;

export const Inspector = ({ open = false, onClose }: { open?: boolean; onClose?: () => void }) => {
  const { project, updateProject } = useProjectStore();
  const tile = project.tiles[project.selectedTile];
  const calculation = calculateProject(project);
  const materials = calculateMaterials(project);
  const dimensions = getSurfaceDimensions(project, project.selectedSurface);
  const glue = materials.consumables.find((line) => line.kind === 'glue');
  const grout = materials.consumables.find((line) => line.kind === 'grout');

  const update = (updater: (draft: ProjectState) => void) => {
    updateProject((draft) => {
      updater(draft);
      return draft;
    });
  };

  const updateTile = (updater: (selected: NonNullable<typeof tile>) => void) => {
    if (!tile) return;
    update((draft) => {
      const selected = draft.tiles[draft.selectedTile];
      if (selected) updater(selected);
    });
  };

  return (
    <aside className={`inspector${open ? ' open' : ''}`}>
      {onClose && (
        <button type="button" className="inspector-close" onClick={onClose} aria-label="Скрыть свойства">
          <Icon name="x" />
        </button>
      )}
      <div className="inspector-scroll">
        <SectionTitle>Свойства</SectionTitle>
        <div className="selection-card">
          <span className="selection-thumb">{tile && <img src={tile.imagePath} alt="" />}</span>
          <span>
            <small>{categoryLabels[project.selectedTile]}</small>
            <strong>{tile?.name ?? 'Не загружена'}</strong>
            <em>{tile?.sku || 'Без артикула'}</em>
          </span>
        </div>

        {tile && (
          <>
            <div className="inspector-group">
              <h3>Размер и фактура</h3>
              <div className="field-grid">
                <Field label="Ширина" type="number" value={tile.tileWidth} suffix="мм" min={10} onChange={(event) => updateTile((selected) => { selected.tileWidth = Number(event.target.value); })} />
                <Field label="Высота" type="number" value={tile.tileHeight} suffix="мм" min={10} onChange={(event) => updateTile((selected) => { selected.tileHeight = Number(event.target.value); })} />
              </div>
              <Field label="Цена за м²" type="number" value={tile.price} suffix="₽" min={0} onChange={(event) => updateTile((selected) => { selected.price = Number(event.target.value); })} />
              <label className="range-control">
                <span><strong>Масштаб фактуры</strong><output>{Math.round(tile.scale * 100)}%</output></span>
                <input type="range" min="25" max="200" value={tile.scale * 100} onChange={(event) => updateTile((selected) => { selected.scale = Number(event.target.value) / 100; })} />
              </label>
              <div className="segmented">
                {([0, 90, 180, 270] as const).map((rotation) => (
                  <button type="button" key={rotation} className={tile.rotation === rotation ? 'active' : ''} onClick={() => updateTile((selected) => { selected.rotation = rotation; })}>
                    {rotation}°
                  </button>
                ))}
              </div>
              <Toggle label="Зеркальное отражение" checked={tile.mirror} onChange={(checked) => updateTile((selected) => { selected.mirror = checked; })} />
              <Toggle label="Вариация текстуры" checked={tile.variationEnabled} onChange={(checked) => updateTile((selected) => { selected.variationEnabled = checked; })} />
            </div>

            <div className="inspector-group">
              <h3>Шов</h3>
              <Field label="Ширина" type="number" value={project.grout.width} suffix="мм" min={0} max={20} onChange={(event) => update((draft) => { draft.grout.width = Number(event.target.value); })} />
              <div className="color-swatches">
                {['#f2f0eb', '#d7d2ca', '#8d8b86', '#3f4242', '#c9b89b'].map((color) => (
                  <button
                    type="button"
                    key={color}
                    aria-label={`Цвет шва ${color}`}
                    className={project.grout.color === color ? 'active' : ''}
                    style={{ backgroundColor: color }}
                    onClick={() => update((draft) => { draft.grout.color = color; })}
                  />
                ))}
                <input aria-label="Свой цвет шва" type="color" value={project.grout.color} onChange={(event) => update((draft) => { draft.grout.color = event.target.value; })} />
              </div>
            </div>
          </>
        )}

        <div className="inspector-group calculation-card">
          <h3>Расчёт поверхности</h3>
          <div className="dimension-summary">
            <span><small>Размер</small><strong>{dimensions.width} × {dimensions.height} мм</strong></span>
            <span><small>Площадь</small><strong>{formatNumber(calculation.surface.area / 1_000_000, 2)} м²</strong></span>
          </div>
          <dl>
            <div><dt>Целые плитки</dt><dd>{calculation.surface.wholeTiles}</dd></div>
            <div><dt>Подрезки</dt><dd>{calculation.surface.cutTiles}</dd></div>
            <div><dt>С запасом {project.wastePercent}%</dt><dd>{formatNumber(calculation.requiredArea, 2)} м²</dd></div>
            <div className="total"><dt>Стоимость плитки</dt><dd>{formatNumber(calculation.totalPrice, 0)} ₽</dd></div>
          </dl>
          <label className="waste-row">
            <span>Запас</span>
            <select value={project.wastePercent} onChange={(event) => update((draft) => { draft.wastePercent = Number(event.target.value); })}>
              {[5, 10, 15, 20].map((value) => <option key={value} value={value}>{value}%</option>)}
            </select>
          </label>
        </div>

        <div className="inspector-group calculation-card">
          <h3>Клей и затирка</h3>
          <dl>
            {glue && (
              <>
                <div><dt>Клей · {glue.perM2} кг/м²</dt><dd>{formatNumber(glue.kg, 1)} кг</dd></div>
                <div><dt>Мешков {glue.packSize} кг</dt><dd>{glue.packs} шт · {formatMoney(glue.total)}</dd></div>
              </>
            )}
            {grout && (
              <>
                <div><dt>Затирка · {grout.perM2} кг/м²</dt><dd>{formatNumber(grout.kg, 1)} кг</dd></div>
                <div><dt>Упаковок {grout.packSize} кг</dt><dd>{grout.packs} шт · {formatMoney(grout.total)}</dd></div>
              </>
            )}
            <div><dt>Плитка по всем стенам</dt><dd>{formatMoney(materials.tilesTotal)}</dd></div>
            <div className="total"><dt>Итого по помещению</dt><dd>{formatMoney(materials.total)}</dd></div>
          </dl>
          <p className="section-copy">Площадь облицовки {formatNumber(materials.area, 2)} м², с запасами — {formatNumber(materials.areaWithReserve, 2)} м².</p>
        </div>
      </div>
    </aside>
  );
};
